import { tokens } from '../design/tokens';

const W = 720;
const H = 720;

async function ensureFonts(): Promise<void> {
  // The BootScene already triggers font load at startup, but defensively wait
  // again here in case this runs from a context that boots faster than the
  // Google Fonts CSS resolves.
  try {
    await document.fonts.load('80px "Bungee Inline"');
    await document.fonts.load('700 110px "Familjen Grotesk"');
    await document.fonts.load('500 32px "Familjen Grotesk"');
    await document.fonts.ready;
  } catch {
    // Canvas will fall back to system fonts — visual is degraded but readable.
  }
}

export async function buildShareImage(args: {
  level: number;
  date: string;
  urlBase: string;
}): Promise<Blob | null> {
  await ensureFonts();

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Background — vertical gradient matching game tokens.
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, tokens.colors.bg.from);
  grad.addColorStop(1, tokens.colors.bg.to);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Subtle rounded frame so the image reads as a card, not a screenshot.
  ctx.strokeStyle = tokens.colors.ui.accent;
  ctx.globalAlpha = 0.35;
  ctx.lineWidth = 2;
  roundRect(ctx, 24, 24, W - 48, H - 48, 18);
  ctx.stroke();
  ctx.globalAlpha = 1;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // LAVASCEND wordmark
  ctx.fillStyle = tokens.colors.ui.text;
  ctx.font = '80px "Bungee Inline", "Impact", sans-serif';
  ctx.fillText('LAVASCEND', W / 2, 180);

  // Flame emoji
  ctx.font = '72px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';
  ctx.fillText('🔥', W / 2, 290);

  // LEVEL N — the headline
  ctx.fillStyle = tokens.colors.ball.core;
  ctx.font = '700 110px "Familjen Grotesk", system-ui, sans-serif';
  ctx.fillText(`LEVEL ${args.level}`, W / 2, 440);

  // Date — muted
  ctx.fillStyle = tokens.colors.ui.textMuted;
  ctx.font = '500 32px "Familjen Grotesk", system-ui, sans-serif';
  ctx.fillText(args.date, W / 2, 520);

  // URL footer
  ctx.fillStyle = tokens.colors.ui.textMuted;
  ctx.font = '500 28px "Familjen Grotesk", system-ui, sans-serif';
  ctx.fillText(args.urlBase, W / 2, 660);

  return new Promise(resolve => {
    canvas.toBlob(blob => resolve(blob), 'image/png');
  });
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
